import { ColorTarget, EncField, EncSeries, Encoding, VariableType } from '@mode-vegas/vegas';
import { ChartTypes, OrgTypes } from '@mode/shared/contract-common';
import { CompiledFormula } from '@mode/shared/util-switch';
import { hsl } from 'd3-color';
import { interpolateRgb, piecewise } from 'd3-interpolate';
import produce from 'immer';
import { RenderDataModel } from '../schema-model';
import * as ChartTheme from './chart-theme';
interface ColorFieldDraftEntry {
  colorPaletteToken: string;
  palette: string[];
  map?: Record<string, number>;
  gradient?: ChartTypes.ContinuousGradient;
  unassignedColor?: string;
  colorTarget?: ColorTarget;
}
interface ColorFieldDraft {
  [k: string]: ColorFieldDraftEntry;
}

export const DEFAULT_UNASSIGNED_COLOR = '#e4e7ed';

export function computePalette(format: ChartTypes.ChartFormat) {
  const {
    colors: { custom, theme },
  } = format;
  if (!custom || !custom.length) {
    return ChartTheme.getTheme(theme).colors;
  }
  return custom;
}

export function getColorMapKeyByField(field: EncField): string {
  return getColorMapKey(field.formula, field.variable.type);
}

export function getColorMapKey(formula: string, type: string): string {
  return `${type === VariableType.DISCRETE ? '@DIS' : '@CON'}_${formula}`;
}

export function resolveFieldColor(
  formula: CompiledFormula | undefined,
  fieldColorMap: Readonly<ChartTypes.FieldColorMap> | undefined
) {
  // No formula, just return undefined
  if (formula == null) {
    return;
  }
  // No field colors, return the default color if there is one.
  if (fieldColorMap == null) {
    return formula.defaultColor;
  }
  // Check for a field color in the map
  const colorMapKey = getColorMapKeyByField(formula.toEncField());
  const fieldColor = fieldColorMap[colorMapKey];
  // No field color, return the default color if there is one.
  if (fieldColor == null) {
    return formula.defaultColor;
  }
  // No default, return the field color if there is one.
  if (formula.defaultColor == null) {
    return fieldColor;
  }

  // There is both a field color and a default color.
  //
  // The discrete color map is the only part of the fieldColor that could be merged.
  // The two maps could include completely different color palettes so the indices
  // can't just be merged.
  const defaultColorPalette = formula.defaultColor.palette ?? [];
  const fieldColorPalette = fieldColor.palette ?? [];
  // Create a set of unique colors
  const colors = new Set([...fieldColorPalette, ...defaultColorPalette]);
  // Generate a new palette from the colors
  const palette = Array.from(colors);
  // Build a map of color to index
  const colorIndexMap = palette.reduce((map, color, index) => {
    map.set(color, index);
    return map;
  }, new Map<string, number>());
  // Build the new merged map.
  const mapping = {} as ChartTypes.ColorEntries;
  // Map the values (ex: 'West') to the color index in the new combined palette.
  // This finds the color from the index in the old palette and then uses the color to get the index
  // in the new palette.
  const buildMapping = (colorEntries: Readonly<ChartTypes.ColorEntries>, paletteColors: ReadonlyArray<string>) => {
    Object.entries(colorEntries).forEach(([value, colorIndex]) => {
      if (colorIndex < 0) {
        mapping[value] = colorIndex;
      } else {
        const color = paletteColors[colorIndex];
        mapping[value] = colorIndexMap.get(color) ?? -1;
      }
    });
  };
  buildMapping(formula.defaultColor.map ?? {}, defaultColorPalette);
  buildMapping(fieldColor.map ?? {}, fieldColorPalette);

  return { ...fieldColor, palette, map: mapping };
}

export function addFieldColor(
  field: EncField,
  fieldColor: ChartTypes.FieldColor,
  fieldColorMap: Readonly<ChartTypes.FieldColorMap> | undefined
) {
  const key = getColorMapKeyByField(field);
  return {
    ...(fieldColorMap ?? {}),
    [key]: fieldColor,
  };
}

export function generateSeriesColorMapping(
  renderModel: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  palette: string[],
  series?: string
) {
  const unformattedDomainValues = renderModel.getColorDomain(encoding, format, series, false);
  const colorMap: ChartTypes.ColorEntries = {};
  unformattedDomainValues.forEach((domainValue: string | null | undefined, i) => {
    if (domainValue != null) {
      colorMap[domainValue] = i % palette.length;
    }
  });
  return colorMap;
}

/*
  The purpose of this logic is to assign the next available/unused color to the colorMap
  The first step we do is create an array with the available indexes to pick from, we do this
  using the palette length.
  We loop the current colorMap and remove the index from the previous array, if the index is higher
  than the palette length we save it for later mapping on the noMappingDomains.
  Then we filter the domains that have no mapping from the domainList and do a merge with the ones
  that were out of index, this merge it's done like this in order to preserve the order of the elements.
  As last step we loop through the noMappingDomains and use the getNextIndex function to pull values
  from the availableIndex array, if the array is empty we repopulated like in step one.
*/
export function buildMuzeDomainRangeMap(
  fieldColor: ChartTypes.FieldColor | undefined,
  palette: readonly string[],
  domainList: Array<string | number>
): { [domainItem: string]: string } {
  const UNASSIGNED_COLOR_DOMAIN_LIMIT = 250;
  const domainRangeMap: { [domainItem: string]: string } = {};
  const colorMapping = fieldColor?.map;
  const unassignedColor = fieldColor?.unassignedColor ?? DEFAULT_UNASSIGNED_COLOR;
  let noMappingDomains: Array<string | number> = [];
  let availableIndexes: number[] = Array.from(Array(palette.length).keys());
  if (colorMapping) {
    for (const [key, value] of Object.entries(colorMapping)) {
      if (value < 0) {
        domainRangeMap[key] = unassignedColor;
      } else if (value < palette.length) {
        const index = availableIndexes.indexOf(value);
        index !== -1 && availableIndexes.splice(index, 1);
        domainRangeMap[key] = palette[value];
      } else {
        key && noMappingDomains.push(key);
      }
    }
  }
  const unassignedDomainItems = domainList.filter((domainItem) => colorMapping?.[domainItem] === undefined);
  const getNextIndex = (): number => {
    if (availableIndexes.length === 0) {
      availableIndexes = Array.from(Array(palette.length).keys());
    }
    return availableIndexes.shift() || 0;
  };
  // Only assign first 250 unmapped colors
  const mappedSize = Object.keys(domainRangeMap).length;
  const availableCount = Math.max(0, UNASSIGNED_COLOR_DOMAIN_LIMIT - mappedSize);
  noMappingDomains = [...noMappingDomains, ...unassignedDomainItems].slice(0, availableCount);

  noMappingDomains.forEach((rawValue) => {
    const index = getNextIndex();
    domainRangeMap[rawValue] = palette[index];
  });

  return domainRangeMap;
}

export function createFieldColors(
  snapshot: ChartTypes.ChartSnapshot,
  action: {
    colorPaletteToken: string;
    colorMap: Record<string, number>;
    palette: string[];
    varType: VariableType;
    formula: string;
    gradient?: ChartTypes.ContinuousGradient;
    unassignedColor?: string;
  }
) {
  return produce(snapshot.fieldColors || {}, (draft: ColorFieldDraft) => {
    draft[getColorMapKey(action.formula, action.varType)] = {
      colorPaletteToken: action.colorPaletteToken,
      palette: action.palette,
      map: action.colorMap,
      gradient: action.gradient,
      unassignedColor: action.unassignedColor,
    };
  });
}

/**
 * Returns a fieldColor map with default color assignments for any new
 * values in the data. Existing values are preserved. New values are
 * assigned new colors from the palette.
 */
export function updateChartFieldColorMap(
  renderModel: RenderDataModel,
  encoding: Encoding,
  format: ChartTypes.ChartFormat,
  fieldColorMap: ChartTypes.FieldColorMap,
  colorPaletteToken: string
) {
  const palette = [...computePalette(format)];
  // Only create mapping for fields which are used for color.
  const colorEncFields: Array<{ field: EncField | undefined; series: string | undefined }> = [];
  if (encoding.marks.channel === '*') {
    colorEncFields.push({ field: encoding.marks.properties.color.values[0], series: undefined });
  } else {
    encoding.marks.series.forEach((series) => {
      colorEncFields.push({ field: series.properties.color.values[0], series: series.field });
    });
  }
  const newFieldColorMap: ChartTypes.FieldColorMap = {};
  colorEncFields.forEach(({ field, series }) => {
    if (field?.variable.type === VariableType.CONTINUOUS) {
      const compiledColorField = renderModel.compileEncField(field);
      const fieldColor = resolveFieldColor(compiledColorField, fieldColorMap);
      if (fieldColor !== undefined) {
        const key = getColorMapKeyByField(field);
        newFieldColorMap[key] = {
          ...fieldColor,
        };
      }
    } else if (field !== undefined) {
      const colorEntryMap: ChartTypes.ColorEntries = {};
      const domainValues = renderModel.getColorDomain(encoding, format, series, false);
      const compiledColorField = renderModel.compileEncField(field);
      const fieldColor = resolveFieldColor(compiledColorField, fieldColorMap);
      const key = getColorMapKeyByField(field);
      if (fieldColor !== undefined) {
        // We need to merge the colors. If there is an existing mapping, then keep the colors
        // in the existing mapping and add new ones
        const currentColorMap = fieldColor.map;
        const entryPalette = fieldColor.palette ?? palette;
        const colorEntries: ChartTypes.ColorEntries = { ...currentColorMap };
        // Count the number of times each palette color is used so that we are less likely to repeat existing colors.
        const histogram = Object.keys(colorEntries).reduce((counts: Array<number>, colorKey: string) => {
          const paletteIndex = colorEntries[colorKey];
          counts[paletteIndex] = (counts[paletteIndex] ?? 0) + 1;
          return counts;
        }, []);
        let mapSize = Object.keys(colorEntries).length;
        // Go through all of the current domain values and add the value if necessary
        domainValues.some((value) => {
          if (mapSize > ChartTypes.COLOR_DOMAIN_DATA_LIMIT) {
            return true;
          }
          const valueKey = value ?? 'null';
          if (!Object.prototype.hasOwnProperty.call(currentColorMap, valueKey)) {
            // Add this to the color map
            const getNextColorPaletteId = () => {
              return entryPalette.reduce((best, current, index) => {
                return (histogram[index] ?? 0) < (histogram[best] ?? 0) ? index : best;
              }, 0);
            };
            const colorPaletteId = getNextColorPaletteId();
            colorEntries[valueKey] = colorPaletteId;
            histogram[colorPaletteId] = (histogram[colorPaletteId] ?? 0) + 1;
            mapSize += 1;
          }
          return false;
        });
        // Update the field colors in the map
        newFieldColorMap[key] = {
          colorPaletteToken: colorPaletteToken,
          palette: entryPalette,
          map: colorEntries,
          unassignedColor: fieldColor.unassignedColor,
        };
      } else {
        // No existing colors. Build a new map using the palette.
        domainValues.some((domainValue: string | null | undefined, i) => {
          colorEntryMap[domainValue ?? 'null'] = i % palette.length;
          return i >= ChartTypes.COLOR_DOMAIN_DATA_LIMIT;
        });
        // Add the mapping to the map
        newFieldColorMap[key] = {
          colorPaletteToken: colorPaletteToken,
          palette: palette,
          map: colorEntryMap,
        };
      }
    }
  });
  return newFieldColorMap;
}

// The initial implementation of continuous color gradients only allowed the user to pick a single color.
// This color represents the midpoint of the gradient with the lower and upper calculated by adding/subtracting
// lightness.
export function createDefaultContinuousGradient(midpointColor: string): ChartTypes.ContinuousGradient {
  const gradientMap: ChartTypes.GradientColorEntries = { [ChartTypes.GradientStep.DATA_MIDPOINT]: midpointColor };
  return {
    gradientType: ChartTypes.GradientType.SEQUENTIAL,
    colorEntries: gradientMap,
  };
}

/**
 * Generates an array containing the colors for a continuous gradient legend.
 *
 * If the optional gradient is not passed, then a two-color range is generated
 * from the specified color. The color is assumed to be the midpoint of the
 * returned gradient. The start and end colors are determined by increasing/decreasing
 * the lightness of the color as HSL.
 *
 * If the gradient is specified, but it does not contain the min and max color entries,
 * then the midpoint color will be used to generate the start and end colors. This
 * case is necessary to handle how some existing gradients are stored (e.g. for fields in
 * Measure Values).
 *
 * If the gradient is passed with valid min/max entries, this will return either
 * a two-color range for a sequential gradient or a three-color range for a diverging gradient.
 * The colors in the range are taken directly from the ContinuousGradient.
 */
export function generateSequentialColorRange(color: string, gradient?: ChartTypes.ContinuousGradient) {
  const hslColor = hsl(color);
  let light = hslColor;
  let midpoint = hslColor;
  let dark = hslColor;
  if (gradient == null || (gradient.colorEntries.DATA_MIN == null && gradient.colorEntries.DATA_MAX == null)) {
    const midpointColor = hsl(gradient?.colorEntries[ChartTypes.GradientStep.DATA_MIDPOINT] ?? color);
    const maxLightnessShift = Math.min(midpointColor.l, 1 - midpointColor.l);
    light = hsl(midpointColor.h, midpointColor.s, midpointColor.l + maxLightnessShift * 0.6);
    dark = hsl(midpointColor.h, midpointColor.s, midpointColor.l - maxLightnessShift * 0.6);
    midpoint = midpointColor;
  } else if (gradient != null) {
    light = hsl(gradient.colorEntries.DATA_MIN ?? color);
    dark = hsl(gradient.colorEntries.DATA_MAX ?? color);
    midpoint = hsl(gradient.colorEntries.DATA_MIDPOINT ?? color);
  }
  return gradient?.gradientType === ChartTypes.GradientType.DIVERGING ? [light, midpoint, dark] : [light, dark];
}

/**
 * Given an Array of colors which form a continuous gradient, this function
 * generates 'steps' number of discrete colors evenly spaced along the gradient.
 * @param colors - RGB color strings (Ex: '#aabbcc'). Minimum 2.
 * @param steps - Number of discrete colors to generate along gradient
 * @returns Array of RGB color strings
 */
export function generateGradientStepColors(colors: string[], steps: number) {
  if (colors.length < 2) {
    throw new Error('At least 2 colors are required to generate steps.');
  }
  const interpolator = piecewise(interpolateRgb, colors);
  const stepColors: string[] = [];
  for (let i = 0; i < steps; i++) {
    const rgbColor = interpolator(i / (steps - 1));
    stepColors.push(hsl(rgbColor).formatHex());
  }
  return stepColors;
}

export function buildDefaultContinuousFieldColor(
  encoding: Readonly<Encoding>,
  renderModel: RenderDataModel | undefined,
  series: Readonly<EncSeries> | undefined,
  fieldColorMap: ChartTypes.FieldColorMap | undefined,
  allColorPalettes: OrgTypes.ColorPalette[]
) {
  // Find the color field in the encoding
  let colorField: EncField | undefined;
  if (encoding.marks.channel === '*') {
    colorField = encoding.marks.properties.color.values[0];
  } else {
    colorField =
      series != null
        ? encoding.marks.series.find((s) => s.field === series.field)?.properties.color.values[0]
        : encoding.marks.series[0]?.properties.color.values[0];
  }
  // If we can't find the color field, return
  if (colorField == null) {
    return;
  }

  // Look for the current FieldColor either by:
  //  - resolving it from the CompiledFormula and FieldColorMap (if there is a RenderDataModel), or
  //  - directly from the FieldColorMap (if there is no RenderDataModel)
  const fieldKey = getColorMapKeyByField(colorField);
  const currentFieldColor =
    renderModel != null
      ? resolveFieldColor(renderModel.compileEncField(colorField), fieldColorMap)
      : fieldColorMap?.[fieldKey];

  // If there is already a field color for this field, just return.
  if (currentFieldColor) {
    return;
  }

  // We need to seed the field colors with the default sequential palette.
  const sequentialPalettes = allColorPalettes.filter(
    (palette) => palette.type === OrgTypes.ColorPaletteType.Sequential
  );

  // Get the default sequential palette
  const defaultSequential = sequentialPalettes?.find((palette) => palette.isDefault) ?? sequentialPalettes[0];
  if (defaultSequential == null) {
    return;
  }

  // Build a continuous field color based on the sequential palette
  const paletteColors = defaultSequential.value.split(',').map((color) => color.trim().toUpperCase());
  const fieldColor: ChartTypes.FieldColor = {
    colorPaletteToken: defaultSequential.token,
    palette: paletteColors,
    map: {},
    gradient: {
      gradientType: ChartTypes.GradientType.SEQUENTIAL,
      colorEntries: {
        [ChartTypes.GradientStep.DATA_MIN]: paletteColors[0],
        [ChartTypes.GradientStep.DATA_MAX]: paletteColors[1],
      },
    },
  };

  return { [fieldKey]: fieldColor };
}
